import Hashids from 'hashids';

// Create hashids instance with salt and minimum length
const hashids = new Hashids('liwaza-salt', 6);

/**
 * Encode a coach ID to a hash
 * @param {number} coachId - The numeric coach ID
 * @returns {string} - The encoded hash
 */
export const encodeCoachId = (coachId) => {
  if (!coachId || isNaN(coachId)) {
    throw new Error('Invalid coach ID provided for encoding');
  }
  return hashids.encode(parseInt(coachId, 10));
};

/**
 * Decode a hash back to coach ID
 * @param {string} hash - The encoded hash
 * @returns {number} - The decoded coach ID
 */
export const decodeCoachId = (hash) => {
  if (!hash || typeof hash !== 'string') {
    throw new Error('Invalid hash provided for decoding');
  }

  const decoded = hashids.decode(hash);
  if (decoded.length === 0) {
    throw new Error('Invalid hash - could not decode');
  }

  return decoded[0];
};

/**
 * Generate coach profile URL with encoded ID
 * @param {number} coachId - The numeric coach ID
 * @returns {string} - The coach profile URL with encoded ID
 */
export const generateCoachProfileUrl = (coachId) => {
  const encodedId = encodeCoachId(coachId);
  return `/coach/${encodedId}`;
};

export const generateCoachInviteUrl = (coachProfileId, origin = '') => {
  const encodedId = encodeCoachId(coachProfileId);
  const base = origin || (typeof window !== 'undefined' ? window.location.origin : '');
  return `${base}/signup?coach_id=${encodedId}`;
};

/**
 * Check if a string looks like a valid hashid
 * @param {string} hash - The hash to validate
 * @returns {boolean} - Whether the hash appears valid
 */
export const isValidCoachHash = (hash) => {
  try {
    decodeCoachId(hash);
    return true;
  } catch {
    return false;
  }
};

/**
 * Encode a post ID to a hash
 * @param {number} postId - The numeric post ID
 * @returns {string} - The encoded hash
 */
export const encodePostId = (postId) => {
  if (!postId || isNaN(postId)) {
    throw new Error('Invalid post ID provided for encoding');
  }
  return hashids.encode(parseInt(postId, 10));
};

/**
 * Decode a hash back to post ID
 * @param {string} hash - The encoded hash
 * @returns {number} - The decoded post ID
 */
export const decodePostId = (hash) => {
  if (!hash || typeof hash !== 'string') {
    throw new Error('Invalid hash provided for decoding');
  }

  const decoded = hashids.decode(hash);
  if (decoded.length === 0) {
    throw new Error('Invalid hash - could not decode');
  }

  return decoded[0];
};

/**
 * Generate post URL with encoded ID
 * @param {number} postId - The numeric post ID
 * @returns {string} - The post URL with encoded ID
 */
export const generatePostUrl = (postId) => {
  const encodedId = encodePostId(postId);
  return `/community/posts/${encodedId}`;
};

/**
 * Check if a string looks like a valid post hashid
 * @param {string} hash - The hash to validate
 * @returns {boolean} - Whether the hash appears valid
 */
export const isValidPostHash = (hash) => {
  try {
    decodePostId(hash);
    return true;
  } catch {
    return false;
  }
};

/**
 * Generic encode function for any ID
 * @param {number} id - The numeric ID
 * @returns {string} - The encoded hash
 */
export const encodeHashId = (id) => {
  if (!id || isNaN(id)) {
    throw new Error('Invalid ID provided for encoding');
  }
  return hashids.encode(parseInt(id, 10));
};

/**
 * Generic decode function for any hash
 * @param {string} hash - The encoded hash
 * @returns {number} - The decoded ID
 */
export const decodeHashId = (hash) => {
  if (!hash || typeof hash !== 'string') {
    throw new Error('Invalid hash provided for decoding');
  }

  const decoded = hashids.decode(hash);
  if (decoded.length === 0) {
    throw new Error('Invalid hash - could not decode');
  }

  return decoded[0];
};

/**
 * Generate resource URL with encoded ID
 * @param {number} resourceId - The numeric resource ID
 * @returns {string} - The resource URL with encoded ID
 */
export const generateResourceUrl = (resourceId) => {
  const encodedId = encodeHashId(resourceId);
  return `/resources/${encodedId}`;
};

/**
 * Debug helper to log URL conversions
 * @param {number} coachId - The numeric coach ID
 * @returns {object} - Debug information
 */
export const debugHashConversion = (coachId) => {
  const encoded = encodeCoachId(coachId);
  const url = generateCoachProfileUrl(coachId);
  const decoded = decodeCoachId(encoded);

  const result = {
    originalId: coachId,
    encodedHash: encoded,
    generatedUrl: url,
    decodedId: decoded,
    isValid: decoded === parseInt(coachId, 10),
  };

  console.log('Hash ID Debug:', result);
  return result;
};
